/**
 * useSecondPulse Hook
 *
 * Shared wall-clock second-boundary pulse for synchronized clock displays.
 * A single module-level scheduler fires once per wall-clock second, aligned
 * to :000ms boundaries using self-correcting setTimeout (no setInterval drift).
 *
 * All subscribers receive the same tick in the same microtask — multiple
 * clock components update together with no independent-loop desync.
 *
 * Lifecycle is reference-counted:
 * - First subscriber starts the scheduler
 * - Last unsubscriber stops it (no idle timers)
 * - React Strict Mode safe (each mount/unmount adds/removes independently)
 *
 * Components handle their own initial render via a mount effect — this hook
 * only provides ongoing aligned ticks after the first second boundary.
 *
 * @see CODING_STANDARDS §2.2 — subscription-based pattern (render-body ref sync)
 * @see components/TradingHub/ElapsedTimer.tsx — consumer
 * @see components/TradingHub/LiveTrading/widgets/TimeOfDayClock.tsx — consumer
 */

import { useEffect, useRef } from 'react';

// ===== TYPES =====

type SecondPulseListener = (tickAtMs: number) => void;

// ===== MODULE-LEVEL SHARED PULSE =====

/** Active listeners — iterated each tick */
const listeners = new Set<SecondPulseListener>();

/** Current scheduled timeout (undefined when scheduler is idle) */
let timerId: ReturnType<typeof setTimeout> | undefined;

/**
 * Schedule the next tick at the upcoming wall-clock second boundary.
 * Self-correcting: each cycle recalculates delay from Date.now(), so
 * accumulated event-loop jitter never compounds across ticks.
 */
function scheduleNextTick(): void {
  const delay = 1000 - (Date.now() % 1000);
  timerId = setTimeout(() => {
    timerId = undefined;

    // Capture timestamp once — all subscribers receive the same value,
    // eliminating per-subscriber Date.now() timing skew.
    const tickAtMs = Date.now();

    // Invoke all listeners with per-listener fault isolation —
    // one throwing subscriber must not prevent others from ticking.
    listeners.forEach((fn) => {
      try {
        fn(tickAtMs);
      } catch {
        // Subscriber error — silently swallowed. The failing component
        // will surface its own error via React's error boundary.
      }
    });

    // Reschedule only if subscribers remain (prevents orphaned timers)
    if (listeners.size > 0) {
      scheduleNextTick();
    }
  }, delay);
}

/**
 * Add a listener to the shared pulse. Returns an unsubscribe function.
 * Starts the scheduler on first subscriber; stops on last unsubscriber.
 */
function subscribe(listener: SecondPulseListener): () => void {
  const wasEmpty = listeners.size === 0;
  listeners.add(listener);

  if (wasEmpty) {
    scheduleNextTick();
  }

  return () => {
    listeners.delete(listener);

    if (listeners.size === 0 && timerId !== undefined) {
      clearTimeout(timerId);
      timerId = undefined;
    }
  };
}

// ===== HOOK =====

/**
 * Subscribe to the shared wall-clock second-boundary pulse.
 *
 * The callback fires once per wall-clock second, aligned to :000ms boundaries.
 * All subscribers fire on the same tick — both clocks update together.
 *
 * Callback ref is synced in the render body (not in effects) per
 * CODING_STANDARDS §2.2 (subscription-based pattern).
 *
 * @param callback - Invoked on each second-boundary tick with the shared timestamp
 */
export function useSecondPulse(callback: SecondPulseListener): void {
  const callbackRef = useRef(callback);
  callbackRef.current = callback; // render-body sync per §2.2

  useEffect(() => {
    return subscribe((tickAtMs) => callbackRef.current(tickAtMs));
  }, []);
}
